// © Microsoft Corporation. All rights reserved.

import { SocialPopover, SocialPopoverProps } from "@msnews/social-popover";
import { ReactionMenuTelemetryObjects, reactionMenuTelemetryActionDispatchers, reactionMenuTelemetryContracts } from "./ReactionMenu.telemetry";
import { ReactionToBehaviorTypeMap, TelemetryEventMap, addOrUpdateTelemetryObject, dispatchTelemetryEvent, getHeadlineByReactionType, getPageContentType, renderHybrid } from "@msnews/social-utils";
import { enums, renderEmojiByType } from "@msnews/social-utils";

import React from "react";
import { ReactionMenuTouchSlideHandler } from "@msnews/social-interaction-helpers";
import { SocialBarLocalizedStrings } from "../../SocialBar.localizedStrings";
import { SocialBarClassNameContract } from "../../SocialBar.classnames";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { classNames } from "@microsoft/fast-web-utilities";

export interface ReactionMenuProps extends SocialPopoverProps {
    onReaction: (key: string) => void;
    onShow?: (target: any, isHover: boolean) => void;
    mainTelemetryObject?: TelemetryObjectBase;
    t: SocialBarLocalizedStrings;
    rating?: string;
    triggerElements?: Array<HTMLElement>;
    visible?: boolean;
    managedClasses?: SocialBarClassNameContract;
}
export default class ReactionMenu extends React.Component<ReactionMenuProps> {
    public static lastHiddenTime = 1;
    public static touchEnabled = false;

    telemetryObjects: ReactionMenuTelemetryObjects;
    containerRef: HTMLDivElement;
    triggerRef: HTMLElement;
    touchSlideHandler: ReactionMenuTouchSlideHandler;

    constructor(props) {
        super(props);
        this.containerRef = null;
        this.triggerHoverHandler = this.triggerHoverHandler.bind(this);
        this.triggerTouchStartHandler = this.triggerTouchStartHandler.bind(this);
        this.triggerClickHandler = this.triggerClickHandler.bind(this);
        this.touchSlideHandler = new ReactionMenuTouchSlideHandler({
            onSlideStart: (target) => {
                ReactionMenu.touchEnabled = true;
                this.props.onShow && this.props.onShow(target, true);
            },
            onSlideEnd: () => this.hide(),
            onLongpress: (target) => {
                ReactionMenu.touchEnabled = true;
                this.props.onShow && this.props.onShow(target, true);
            },
            onLongpressEnd: () => this.hide(),
            onTouchEnd: () => { ReactionMenu.touchEnabled = false; }
        },
        {
            onEmojiFocus: (el: HTMLElement) => el.classList.add("touchFocused"),
            onEmojiBlur: (el: HTMLElement) => el.classList.remove("touchFocused")
        }, ".cwt-link.cwt-icon");
    }

    componentDidMount() {
        if (this.containerRef) {
            dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.containerRef);
        }

        this.registerHandlers(this.props.triggerElements);
    }

    componentDidUpdate(nextProps: ReactionMenuProps) {
        this.registerHandlers(this.props.triggerElements);
    }

    componentWillUnmount() {
        this.touchSlideHandler.dispose();
        this.unregisterHandlers(this.props.triggerElements);
    }

    render() {
        if (!this.props.visible || (this.props.isHover && !this.triggerRef)) { return null; }
        const {t, isHover, managedClasses} = this.props;

        this.generateOrUpdateTelemetryObjects();

        return (
            <SocialPopover {...{...this.props, triggerRef: { current: this.triggerRef }, className: classNames(managedClasses.reactionMenuPopover, this.props.className), hasFocusLock: !isHover && !ReactionMenu.touchEnabled, marginTop: 40}}>
                <div className={classNames(managedClasses.reactionMenuContainer, "cwt-reaction-menu")} role="tablist" {...renderHybrid(this.telemetryObjects && this.telemetryObjects.container, true, (el) => { this.containerRef = el; })}>
                    {enums.reactionOrder.map((key) =>
                        <i
                            key={key} className="cwt-link cwt-icon"
                            role="tab"
                            aria-label={t[`EmojiLabel${key}`]}
                            onClick={(e) => this.reactionClickHandler(e.currentTarget, key)}
                            data-title={t[`EmojiLabel${key}`]}
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.emojis && this.telemetryObjects.emojis[key], false)}
                        >
                            {renderEmojiByType(key)}
                        </i>
                    )}
                </div>
            </SocialPopover>
        );
    }

    // :: ReactionMenu specific methods
    reactionClickHandler(target, key) {
        if (!this.props.visible) { return; }

        reactionMenuTelemetryActionDispatchers.emojiClick(target, this.telemetryObjects && this.telemetryObjects.emojis[key]);
        this.props.onReaction(key);
        this.hide();
    }
    hide() {
        ReactionMenu.lastHiddenTime = (new Date()).getTime();
        this.props && this.props.onHide && setTimeout(() => { this.props.onHide(); }, 10);
    }

    // :: Trigger element handlers
    triggerHoverHandler(e) {
        this.triggerRef = e.currentTarget;
        this.setState({});

        if ((new Date()).getTime() - ReactionMenu.lastHiddenTime < 225) { return; }
        this.props && this.props.onShow && this.props.onShow(e.currentTarget, true);
    }
    triggerTouchStartHandler(e) {
        this.triggerRef = e.currentTarget;
        this.setState({});
        this.touchSlideHandler.setupTriggerAtTouchStart(e);
    }
    triggerClickHandler(e) {
        !ReactionMenu.touchEnabled && this.hide();
    }

    // :: Event attachers to trigger element
    registerHandlers(triggerRefs: Array<HTMLElement>) {
        this.containerRef && this.touchSlideHandler.setReactionMenuContainer(this.containerRef);

        setTimeout(() => {
            triggerRefs && triggerRefs.map(ref => {
                if (!ref || ref.getAttribute("data-trigger-reqistered")) { return; }
                ref.setAttribute("data-trigger-reqistered", "1");
                ref.addEventListener("mouseenter", this.triggerHoverHandler);
                ref.addEventListener("click", this.triggerClickHandler);
                ref.addEventListener("touchstart", this.triggerTouchStartHandler);
            });
        }, 10);
    }
    unregisterHandlers(triggerRefs: Array<HTMLElement>) {
        this.touchSlideHandler.dispose();

        triggerRefs && triggerRefs.map(ref => {
            if (!ref || !ref.getAttribute("data-trigger-reqistered")) { return; }
            ref.removeAttribute("data-trigger-reqistered");
            ref.removeEventListener("mouseenter", this.triggerHoverHandler);
            ref.removeEventListener("touchstart", this.triggerTouchStartHandler);
        });
    }

    generateOrUpdateTelemetryObjects() {
        const { mainTelemetryObject, rating } = this.props;

        if (mainTelemetryObject) {
            const id = mainTelemetryObject.contract.content.id;
            const type = mainTelemetryObject.contract.content.type? mainTelemetryObject.contract.content.type : getPageContentType();

            this.telemetryObjects = {
                container: addOrUpdateTelemetryObject(mainTelemetryObject, {...reactionMenuTelemetryContracts.container}, { id, type }, this.telemetryObjects && this.telemetryObjects.container),
                emojis: []
            };

            enums.reactionOrder.map((key) => {
                const headline = getHeadlineByReactionType(key, key == rating);
                const behavior = ReactionToBehaviorTypeMap[key];
                this.telemetryObjects.emojis[key] = addOrUpdateTelemetryObject(mainTelemetryObject, {...reactionMenuTelemetryContracts.emojis, name: headline, behavior}, { id, type, headline }, this.telemetryObjects && this.telemetryObjects.emojis && this.telemetryObjects.emojis[key]);
            });
        }
    }
}